import { rewriteHtmlImgToWebp } from './config/image-webp';
import { shareAsync } from './share-async';

export type GuideTocItem = { id: string; title: string; level: 1 | 2 | 3 };

export type GuidePostData = {
  title: string;
  content: string;
  excerpt: string;
  author: string;
  authorAvatar: string | null;
  authorLevel: number;
  category: string;
  publishedAt: string | null;
  cover: string | null;
  likeCount: number;
  favoriteCount: number;
  shareCount: number;
};

export function plainHeadingText(value: string) {
  return value
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .replace(/\s+/g, ' ')
    .trim();
}

export function cleanLegacyArticleMarkup(content: string) {
  return content
    // Older editor versions stored H1 as a paragraph containing a styled span.
    .replace(/<(p|div)\b[^>]*>\s*<span\b([^>]*)>([^<>]+)<\/span>\s*<\/\1>/gi, (block, _tag: string, attributes: string, text: string) => {
      if (!/font-size\s*:\s*26px/i.test(attributes) || !/font-weight\s*:\s*(?:600|700|bold)/i.test(attributes)) return block;
      return `<h1>${text}</h1>`;
    })
    // Imported image blocks sometimes sit inside otherwise empty headings.
    .replace(/<(h[123])\b[^>]*>([\s\S]*?)<\/\1>/gi, (block, _tag: string, inner: string) => {
      if (plainHeadingText(inner)) return block;
      return /<(?:img|figure|video|iframe)\b/i.test(inner) ? inner : '';
    })
    .replace(/<div\b[^>]*>(?:\s|&nbsp;|&#160;|&#xA0;|<br\s*\/?>|<\/?(?:span|b|strong|i|em|u|s|font)\b[^>]*>)*<\/div>/gi, '')
    .replace(/<div\b[^>]*class=(['"])[^'"]*\bdocument-empty-block\b[^'"]*\1[^>]*>\s*(?:<br\s*\/?>|&nbsp;|&#160;|&#xA0;|\s)*<\/div>/gi, '')
    .replace(/<p\b[^>]*>([\s\S]*?)<\/p>/gi, (block, inner: string) => {
      // Imported paragraphs may wrap a lone line break in several formatting tags.
      const remaining = inner.replace(/<\/?(?:span|b|strong|i|em|u|s|font)\b[^>]*>|<br\s*\/?>/gi, '').replace(/&nbsp;|&#160;|&#xA0;/gi, '').replace(/[\s\u200b\ufeff]/g, '');
      return remaining ? block : '';
    })
    .replace(/<figcaption\b[^>]*>([\s\S]*?)<\/figcaption>/gi, (block, inner: string) => {
      const remaining = inner.replace(/<br\s*\/?>/gi, '').replace(/&nbsp;|&#160;/gi, '').replace(/[\s\u200b\ufeff]/g, '');
      return remaining ? block : '';
    })
    .replace(
      /<div class="document-empty-block"><div><figure>([\s\S]*?)<\/figure><\/div><\/div>/gi,
      (block, inner: string) => {
        if (!/<svg\s+viewBox="0 0 22 24"/i.test(inner)) return block;
        const remaining = inner
          .replace(/<svg\b[^>]*>[\s\S]*?<\/svg>/gi, '')
          .replace(/<h[1-6]\b[^>]*>\s*<\/h[1-6]>/gi, '')
          .replace(/<\/?div\b[^>]*>/gi, '')
          .trim();
        return remaining ? block : '';
      },
    );
}

export function buildArticleOutline(content: string) {
  const toc: GuideTocItem[] = [];
  const usedIds = new Set<string>();
  let headingIndex = 0;
  const html = rewriteHtmlImgToWebp(cleanLegacyArticleMarkup(content)).replace(
    /<(h[123])([^>]*)>([\s\S]*?)<\/\1>/gi,
    (source, tag: string, attributes: string, inner: string) => {
      const title = plainHeadingText(inner);
      if (!title) return source;
      headingIndex += 1;
      const existingId = attributes.match(/\sid=(['"])(.*?)\1/i)?.[2];
      let id = existingId || `guide-section-${headingIndex}`;
      if (usedIds.has(id)) {
        id = `guide-section-${headingIndex}`;
        while (usedIds.has(id)) id = `guide-section-${++headingIndex}`;
      }
      usedIds.add(id);
      toc.push({ id, title, level: Number(tag.slice(1)) as GuideTocItem['level'] });
      if (existingId && existingId === id) return source;
      if (existingId) {
        return source.replace(/\sid=(['"])(.*?)\1/i, ` id="${id}"`);
      }
      return `<${tag}${attributes} id="${id}">${inner}</${tag}>`;
    },
  );
  return { html, toc };
}

export function normalizeGuidePost(raw: {
  title?: string;
  content?: string;
  excerpt?: string;
  author?: string;
  authorAvatar?: string | null;
  authorLevel?: number;
  category?: string | null;
  publishedAt?: string | null;
  cover?: string | null;
  likeCount?: number;
  favoriteCount?: number;
  shareCount?: number;
}): GuidePostData {
  return {
    title: raw.title || '攻略详情',
    content: raw.content || '',
    excerpt: raw.excerpt || '',
    author: raw.author || '匿名用户',
    authorAvatar: raw.authorAvatar || null,
    authorLevel: raw.authorLevel || 1,
    category: raw.category || '攻略',
    publishedAt: raw.publishedAt || null,
    cover: raw.cover || null,
    likeCount: raw.likeCount || 0,
    favoriteCount: raw.favoriteCount || 0,
    shareCount: raw.shareCount || 0,
  };
}

export function formatGuidePublishedAt(publishedAt: string | null) {
  if (!publishedAt) return '待发布';
  const parts = new Intl.DateTimeFormat('zh-CN', {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: 'Asia/Shanghai',
  }).formatToParts(new Date(publishedAt));
  const getPart = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((part) => part.type === type)?.value ?? '';
  return `${getPart('year')}年${getPart('month')}月${getPart('day')}日 ${getPart('hour')}:${getPart('minute')}`;
}

const guidePostInflight = new Map<string, Promise<GuidePostData>>();
const guideSummaryInflight = new Map<
  string,
  Promise<Array<{ id: string; title: string }>>
>();
const postEditAccessInflight = new Map<string, Promise<boolean>>();

/** 攻略正文（含 StrictMode 并发合并） */
export async function fetchGuidePost(gameKey: string, postId: string) {
  return shareAsync(guidePostInflight, `${gameKey}:${postId}`, async () => {
    const response = await fetch(
      `/api/games/${encodeURIComponent(gameKey)}/posts/${encodeURIComponent(postId)}`,
      { cache: 'no-store', credentials: 'same-origin' },
    );
    if (!response.ok) {
      throw new Error(response.status === 404 ? '攻略不存在或已下线' : '攻略加载失败');
    }
    const postJson = (await response.json()) as Parameters<typeof normalizeGuidePost>[0];
    return normalizeGuidePost(postJson);
  });
}

/** 攻略摘要列表（专辑标题等） */
export async function fetchGuidePostSummaries(gameKey: string) {
  return shareAsync(guideSummaryInflight, gameKey, async () => {
    const response = await fetch(
      `/api/games/${encodeURIComponent(gameKey)}/posts?type=guide&summary=1`,
      { cache: 'no-store', credentials: 'same-origin' },
    );
    if (!response.ok) return [];
    const list = (await response.json()) as {
      posts?: Array<{ id?: string; title?: string }>;
    };
    return (list.posts || [])
      .filter((item): item is { id: string; title?: string } => Boolean(item.id))
      .map((item) => ({ id: item.id, title: item.title || '未命名攻略' }));
  });
}

/** 当前用户是否可编辑已发布帖 */
export async function fetchPostEditAccess(
  gameKey: string,
  postId: string,
  token: string,
) {
  return shareAsync(
    postEditAccessInflight,
    `${gameKey}:${postId}:${token.slice(0, 12)}`,
    async () => {
      const response = await fetch(
        `/api/games/${encodeURIComponent(gameKey)}/posts/${encodeURIComponent(postId)}/edit`,
        { headers: { 'access-token': token } },
      );
      const payload = (await response.json()) as { id?: string; status?: string };
      return response.ok && payload.id === postId && payload.status === 'published';
    },
  );
}
